// Omnix/client/ui/ConversationTimeline.js
import { ComponentBase } from "../core/ComponentBase.js";
import { createAvatarElement } from "../../helpers/avatarHelper.js";
import { marked } from "https://cdn.jsdelivr.net/npm/marked/lib/marked.esm.js";

export class ConversationTimeline extends ComponentBase {
    static styles = `
        /* --- Main Timeline Container --- */
        .timeline-container { 
            display: flex; 
            flex-direction: column; 
            height: 100%; 
            background-color: var(--bs-body-bg); 
            position: relative;
        }
        
        .timeline-header { 
            padding: 0.75rem 1rem; 
            border-bottom: 1px solid var(--bs-border-color); 
            display: flex; 
            align-items: center; 
            gap: 0.75rem; 
            flex-shrink: 0;
            background-color: var(--bs-secondary-bg);
        }
        
        .timeline-header-info { flex-grow: 1; overflow: hidden; }
        .timeline-header-name { font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .timeline-header-subtext { font-size: 0.8rem; color: var(--bs-secondary-color); }
        
        /* --- Message Area with Dynamic Loading --- */
        .timeline-body { 
            flex-grow: 1; 
            overflow-y: scroll; /* Force scrollbar visibility */
            padding: 0.5rem 0;
            display: flex;
            flex-direction: column;
            position: relative;
            min-height: 0; /* Allow flex shrinking */
        }

        .timeline-status-banner {
            padding: 0.5rem 1rem;
            margin: 0.5rem 1rem;
            border-radius: var(--bs-border-radius);
            font-size: 0.9rem;
            flex-shrink: 0;
        }
        .timeline-status-banner .spinner-border-sm {
            width: 1rem;
            height: 1rem;
            border-width: .2em;
        }

        /* Loading indicators */
        .loading-older-indicator, .loading-newer-indicator {
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 1rem;
            color: var(--bs-secondary-color);
            font-size: 0.9rem;
            gap: 0.5rem;
        }

        .loading-older-indicator {
            border-bottom: 1px solid var(--bs-border-color-translucent);
        }

        .loading-newer-indicator {
            border-top: 1px solid var(--bs-border-color-translucent);
        }

        /* Messages container */
        .messages-container {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
            flex-grow: 1; /* Take available space */
            /* Remove justify-content: flex-end to allow natural top-to-bottom flow */
        }

        /* --- Message Bubble Styles --- */
        .timeline-message-wrapper {
            display: flex;
            padding: 0 1rem;
            box-sizing: border-box;
            width: 100%;
            flex-shrink: 0;
        }

        .timeline-message-wrapper.outbound {
            justify-content: flex-end;
        }

        .timeline-message-wrapper.system {
            justify-content: center;
            max-width: 100%;
        }
        .timeline-message-bubble.system {
            background-color: var(--bs-tertiary-bg);
            color: var(--bs-secondary-color);
            font-size: 0.85rem;
            font-style: italic;
            text-align: center;
        }

        .timeline-message-wrapper.inbound {
            justify-content: flex-start;
        }

        .timeline-message-bubble {
            padding: 0.75rem 1rem;
            border-radius: 1rem;
            word-wrap: break-word;
            max-width: 75%;
            display: block;
            text-align: left;
            position: relative;
            box-shadow: 0 1px 2px rgba(0,0,0,0.1);
            animation: messageSlideIn 0.2s ease-out;
        }

        @keyframes messageSlideIn {
            from {
                opacity: 0;
                transform: translateY(10px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }

        .timeline-message-bubble.outbound {
            background-color: var(--bs-primary-bg-subtle);
            color: var(--bs-primary-text-emphasis);
            border-bottom-right-radius: 0.25rem;
        }

        .timeline-message-bubble.inbound {
            background-color: var(--bs-secondary-bg);
            border-bottom-left-radius: 0.25rem;
        }

        .timeline-message-bubble.sending {
            opacity: 0.7;
            position: relative;
        }

        .timeline-message-bubble.sending::after {
            content: '';
            position: absolute;
            top: 50%;
            right: 0.5rem;
            transform: translateY(-50%);
            width: 12px;
            height: 12px;
            border: 2px solid currentColor;
            border-top: 2px solid transparent;
            border-radius: 50%;
            animation: spin 1s linear infinite;
        }

        .timeline-log-entry-wrapper {
            display: flex;
            justify-content: center;
            padding: 0.5rem 1rem;
            width: 100%;
            flex-shrink: 0;
        }
        .timeline-log-entry {
            background-color: var(--bs-tertiary-bg);
            color: var(--bs-secondary-color);
            font-size: 0.85rem;
            font-style: italic;
            text-align: center;
            padding: 0.4rem 0.8rem;
            border-radius: 1rem;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
        }
        .timeline-log-entry .log-icon {
            font-size: 1rem;
        }
        .timeline-log-entry .audio-player {
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }
        .timeline-log-entry .audio-player audio {
            max-width: 250px;
        }

        @keyframes spin {
            0% { transform: translateY(-50%) rotate(0deg); }
            100% { transform: translateY(-50%) rotate(360deg); }
        }

        .timeline-message-bubble.email-bubble {
            max-width: 85%;
        }

        .email-subject {
            font-weight: 600;
            color: var(--bs-emphasis-color);
            margin-bottom: 0.75rem;
            padding-bottom: 0.5rem;
            border-bottom: 1px solid rgba(0,0,0,0.1);
            font-size: 1rem;
        }

        .sender-email {
            font-weight: normal;
            font-size: 0.75rem;
            color: var(--bs-secondary-color);
        }

        .email-content {
            white-space: normal;
            line-height: 1.5;
        }

        .email-content p {
            margin: 0 0 0.75rem 0;
        }

        .email-content p:last-child {
            margin-bottom: 0;
        }

        .email-signature {
            color: var(--bs-secondary-color);
            font-size: 0.9em;
            margin-top: 0.75rem;
            padding-top: 0.5rem;
            border-top: 1px solid rgba(0,0,0,0.1);
        }

        .email-quoted-content {
            margin-top: 1rem;
            padding-top: 0.75rem;
            border-top: 1px solid rgba(0,0,0,0.1);
            color: var(--bs-secondary-color);
            font-size: 0.9em;
        }

        .email-quote-header {
            font-weight: 500;
            margin-bottom: 0.5rem;
            color: var(--bs-emphasis-color);
        }

        .email-original-message {
            background-color: rgba(0,0,0,0.03);
            padding: 0.75rem;
            border-radius: 0.5rem;
            margin-top: 0.5rem;
            border-left: 3px solid var(--bs-primary);
        }

        .message-sender-name {
            font-size: 0.8rem;
            font-weight: 500;
            color: var(--bs-primary);
            margin-bottom: 0.5rem;
            display: block;
        }

        .message-content {
            word-wrap: break-word;
            overflow-wrap: break-word;
            line-height: 1.4;
            margin-bottom: 0.5rem;
        }

        .message-content p {
            margin: 0 0 0.5rem 0;
        }

        .message-content p:last-child {
            margin-bottom: 0;
        }

        .message-content pre {
            background-color: rgba(0,0,0,0.05);
            padding: 0.5rem;
            border-radius: 0.25rem;
            overflow-x: auto;
            margin: 0.5rem 0;
            font-size: 0.9em;
        }

        .message-content code {
            background-color: rgba(0,0,0,0.05);
            padding: 0.1rem 0.3rem;
            border-radius: 0.25rem;
            font-size: 0.9em;
        }

        .message-content blockquote {
            border-left: 3px solid var(--bs-primary);
            padding-left: 1rem;
            margin: 0.5rem 0;
            color: var(--bs-secondary-color);
        }

        .message-timestamp {
            font-size: 0.7rem;
            color: var(--bs-secondary-color);
            text-align: right;
            white-space: nowrap;
            margin-top: 0.25rem;
        }
        
        /* --- Placeholder & Mobile Styles --- */
        .timeline-placeholder { 
            display: flex; 
            flex-direction: column; 
            justify-content: center; 
            align-items: center; 
            height: 100%; 
            color: var(--bs-secondary-color); 
            text-align: center; 
        }
        
        .timeline-placeholder i { 
            font-size: 4rem; 
            margin-bottom: 1rem; 
        }
        
        .mobile-back-btn { 
            display: none; 
            margin-right: 0.5rem; 
        }
        
        @media (max-width: 768px) { 
            .mobile-back-btn { 
                display: inline-block; 
            }
            
            .timeline-message-bubble {
                max-width: 85%;
                padding: 0.6rem 0.8rem;
            }
        }

        /* Unread indicator */
        .unread-indicator {
            background-color: var(--bs-danger);
            color: white;
            padding: 0.25rem 0.75rem;
            border-radius: 1rem;
            font-size: 0.8rem;
            font-weight: 500;
            text-align: center;
            margin: 0.5rem 1rem;
            position: sticky;
            top: 0;
            z-index: 10;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }

        /* ######### COLLAPSIBLE QUOTED SECTION ######### */
        details.email-quoted-content {
            margin-top: 1rem;
        }

        details.email-quoted-content > summary {
            display: flex;
            align-items: center;
            cursor: pointer;
            list-style: none;
            padding-top: 0.75rem;
            border-top: 1px solid rgba(0,0,0,0.1);
            margin: 0;
        }

        details.email-quoted-content > summary::-webkit-details-marker {
            display: none;
        }
        details.email-quoted-content > summary::marker {
         content: none;
        }

        details.email-quoted-content > summary i {
            transition: transform 0.2s ease;
            margin-right: 0.5rem;
        }

        details.email-quoted-content[open] > summary i {
            transform: rotate(180deg);
        }
    `;

    constructor(config) {
        super(config);
        this.conversationData = null;
        this.interactions = [];
        this.messageElements = new Map();
        this.pendingOptimisticMessages = new Map();
        this.isRendering = false;
        this.statusBannerContainer = null;
        this.recordingPlaybackKey = null;

        // Pagination state
        this.pageSize = 50; // Match backend default
        this.currentPage = 1;
        this.totalCount = 0;
        this.hasMoreOlder = true;
        this.hasMoreNewer = false;
        this.isLoadingOlder = false;
        this.isLoadingNewer = false;

        // Scroll management
        this.shouldMaintainScrollPosition = false;
        this.targetScrollPosition = null;
        this.scrollToBottomOnNextRender = false;
        this.lastReadInteractionId = null;

        // Performance optimization
        this.scrollThrottle = null;
        this.renderQueue = [];
        this.isProcessingRenderQueue = false;

        this.applyStyles();
    }

    async initialize() {
        this.container = document.createElement('div');
        this.container.className = 'timeline-container';

        this.viewport = document.createElement('div');
        this.viewport.className = 'timeline-body';
        
        // Use throttled scroll handler
        this.viewport.addEventListener('scroll', this.throttledScrollHandler.bind(this));

        this.messagesContainer = document.createElement('div');
        this.messagesContainer.className = 'messages-container';
        this.viewport.appendChild(this.messagesContainer);

        this.composer = await this.createManagedComponent('DynamicComposer', null, {
            instanceId: `${this.config.instanceId}-composer`
        });
        this.composer.on('messageSendRequested', (payload) => this.emit('messageSendRequested', payload));
        this.composer.on('typingStatusChanged', (payload) => this.emit('typingStatusChanged', payload));

        await super.initialize();
    }

    async initAfterLogin() {
        try {
            const keyInfo = await this.appClient.rpcClient.call('UnifiedInboxRpc.getRecordingPlaybackKey');
            this.recordingPlaybackKey = keyInfo.key;
        } catch (error) {
            this.log.error("Failed to get recording playback key. Audio playback will not work.", error);
        }
    }

    throttledScrollHandler() {
        if (this.scrollThrottle) return;
        
        this.scrollThrottle = setTimeout(() => {
            this.handleScroll();
            this.scrollThrottle = null;
        }, 100);
    }

    handleScroll() {
        if (!this.viewport || this.isLoadingOlder || this.isLoadingNewer) return;

        const scrollTop = this.viewport.scrollTop;
        const scrollHeight = this.viewport.scrollHeight;
        const clientHeight = this.viewport.clientHeight;

        // Load older messages when scrolled to top
        if (scrollTop <= 50 && this.hasMoreOlder) {
            this.loadOlderInteractions();
        }

        // Load newer messages when scrolled to bottom (if we're not at the latest)
        if (scrollTop + clientHeight >= scrollHeight - 50 && this.hasMoreNewer) {
            this.loadNewerInteractions();
        }
    }

    async setConversation(conversation) {
        // Complete state reset
        this.conversationData = conversation;
        this.interactions = [];
        this.messageElements.clear();
        this.pendingOptimisticMessages.clear();
        this.isRendering = false;

        // Reset pagination state
        this.currentPage = 1;
        this.totalCount = 0;
        this.hasMoreOlder = true;
        this.hasMoreNewer = false;
        this.isLoadingOlder = false;
        this.isLoadingNewer = false;
        this.oldestLoadedTimestamp = null;
        this.newestLoadedTimestamp = null;

        // Clear any pending operations
        if (this.scrollThrottle) {
            clearTimeout(this.scrollThrottle);
            this.scrollThrottle = null;
        }

        // Determine initial scroll behavior
        const unreadCount = conversation?.unreadCount || 0;
        if (unreadCount > 0) {
            // If there are unread messages, we'll scroll to first unread
            this.lastReadInteractionId = conversation?.lastReadInteractionId;
        } else {
            // No unread messages, scroll to bottom
            this.scrollToBottomOnNextRender = true;
        }

        await this.loadLatestInteractions();

        // Update composer state
        if (this.composer) {
            const isClosed = conversation?.status === 'closed';
            const isWorkflowControlled = conversation?.controlledBy === 'workflow';
            const isRinging = conversation?.handoffState === 'ringing';

            const myUserId = this.appClient.getSharedData('userId');
            const isAssignedToMe = conversation?.assigneeId === myUserId;
            const isReadOnly = isWorkflowControlled || isRinging || isClosed || (conversation?.assigneeId && !isAssignedToMe);

            let reason = "";
            if (isWorkflowControlled) {
                reason = "This conversation is currently handled by an AI Assistant.";
            } else if (isRinging) {
                reason = "Awaiting agent to accept handoff.";
            } else if (isClosed) {
                reason = "This conversation has been closed.";
            } else if (conversation?.assigneeId && !isAssignedToMe) {
                reason = `Assigned to ${conversation.assigneeDisplayName || 'another agent'}.`;
            }
            this.composer.setDisabled(isReadOnly, this.t(reason));
        }

        this.updateStatusBanners();
    }

    async loadLatestInteractions() {
        if (!this.conversationData || this.isLoadingNewer) return;

        this.isLoadingNewer = true;
        this.renderLoadingIndicator('newer', true);

        try {
            // Get the total count from the existing getInteractions response
            const result = await this.appClient.rpcClient.call('UnifiedInboxRpc.getInteractions', {
                conversationId: this.conversationData._id,
                page: 1,
                pageSize: this.pageSize
            });

            const totalCount = result.totalCount || 0;
            const lastPage = Math.max(1, Math.ceil(totalCount / this.pageSize));
            
            let latestInteractions = [];
            
            if (lastPage === 1) {
                // All messages fit in one page
                latestInteractions = result.data || [];
                this.hasMoreOlder = false;
            } else {
                // Load the last page to get most recent messages
                const lastPageResult = await this.appClient.rpcClient.call('UnifiedInboxRpc.getInteractions', {
                    conversationId: this.conversationData._id,
                    page: lastPage,
                    pageSize: this.pageSize
                });
                latestInteractions = lastPageResult.data || [];
                this.hasMoreOlder = lastPage > 1;
            }

            this.hasMoreNewer = false; // We're at the latest
            this.currentPage = lastPage;
            this.totalCount = totalCount;

            if (latestInteractions.length > 0) {
                this.oldestLoadedTimestamp = latestInteractions[0].timestamp;
                this.newestLoadedTimestamp = latestInteractions[latestInteractions.length - 1].timestamp;
            }

            this.interactions = latestInteractions;
            await this.render();

            // Handle initial scroll positioning
            if (this.scrollToBottomOnNextRender) {
                this.scrollToBottom(true);
                this.scrollToBottomOnNextRender = false;
            } else if (this.lastReadInteractionId) {
                this.scrollToUnreadMessages();
            } else {
                this.scrollToBottom(true);
            }

        } catch (error) {
            this.log.error("Failed to load latest interactions:", error);
            await this.render(); // Render empty state
        } finally {
            this.isLoadingNewer = false;
            this.renderLoadingIndicator('newer', false);
        }
    }

    async loadOlderInteractions() {
        if (!this.conversationData || this.isLoadingOlder || !this.hasMoreOlder) return;

        this.isLoadingOlder = true;
        this.renderLoadingIndicator('older', true);

        // Store current scroll position
        const previousScrollHeight = this.viewport.scrollHeight;
        const previousScrollTop = this.viewport.scrollTop;

        try {
            // Load the previous page
            const previousPage = this.currentPage - 1;
            if (previousPage < 1) {
                this.hasMoreOlder = false;
                return;
            }

            const result = await this.appClient.rpcClient.call('UnifiedInboxRpc.getInteractions', {
                conversationId: this.conversationData._id,
                page: previousPage,
                pageSize: this.pageSize
            });

            const olderInteractions = result.data || [];
            this.hasMoreOlder = previousPage > 1;

            if (olderInteractions.length > 0) {
                this.currentPage = previousPage;
                this.oldestLoadedTimestamp = olderInteractions[0].timestamp;
                
                // Prepend older interactions to maintain chronological order
                this.interactions.unshift(...olderInteractions);
                
                // Re-render with maintained scroll position
                await this.render();
                
                // Restore scroll position relative to the content that was previously visible
                requestAnimationFrame(() => {
                    const newScrollHeight = this.viewport.scrollHeight;
                    const heightDifference = newScrollHeight - previousScrollHeight;
                    this.viewport.scrollTop = previousScrollTop + heightDifference;
                });
            }

        } catch (error) {
            this.log.error("Failed to load older interactions:", error);
        } finally {
            this.isLoadingOlder = false;
            this.renderLoadingIndicator('older', false);
        }
    }

    async loadNewerInteractions() {
        if (!this.conversationData || this.isLoadingNewer || !this.hasMoreNewer) return;

        this.isLoadingNewer = true;
        this.renderLoadingIndicator('newer', true);

        try {
            // Load the next page
            const nextPage = this.currentPage + 1;
            const maxPage = Math.ceil(this.totalCount / this.pageSize);
            
            if (nextPage > maxPage) {
                this.hasMoreNewer = false;
                return;
            }

            const result = await this.appClient.rpcClient.call('UnifiedInboxRpc.getInteractions', {
                conversationId: this.conversationData._id,
                page: nextPage,
                pageSize: this.pageSize
            });

            const newerInteractions = result.data || [];
            this.hasMoreNewer = nextPage < maxPage;

            if (newerInteractions.length > 0) {
                this.currentPage = nextPage;
                this.newestLoadedTimestamp = newerInteractions[newerInteractions.length - 1].timestamp;
                
                // Append newer interactions
                this.interactions.push(...newerInteractions);
                
                // Render new messages without full re-render for better performance
                this.renderNewMessages(newerInteractions);
            }

        } catch (error) {
            this.log.error("Failed to load newer interactions:", error);
        } finally {
            this.isLoadingNewer = false;
            this.renderLoadingIndicator('newer', false);
        }
    }

    renderNewMessages(newInteractions) {
        const startIndex = this.interactions.length - newInteractions.length;
        newInteractions.forEach((interaction, i) => {
            const index = startIndex + i;
            this._addMessageElement(interaction, index);
        });
    }

    renderLoadingIndicator(type, show) {
        const className = `loading-${type}-indicator`;
        let indicator = this.viewport.querySelector(`.${className}`);
        
        if (show) {
            if (!indicator) {
                indicator = document.createElement('div');
                indicator.className = className;
                indicator.innerHTML = `
                    <div class="spinner-border spinner-border-sm" role="status"></div>
                    <span>Loading ${type} messages...</span>
                `;
                
                if (type === 'older') {
                    this.viewport.insertBefore(indicator, this.messagesContainer);
                } else {
                    this.viewport.appendChild(indicator);
                }
            }
        } else {
            if (indicator) {
                indicator.remove();
            }
        }
    }

    scrollToUnreadMessages() {
        if (!this.lastReadInteractionId) {
            this.scrollToBottom(true);
            return;
        }

        // Find the first unread message
        const firstUnreadIndex = this.interactions.findIndex(
            interaction => interaction._id === this.lastReadInteractionId
        );

        if (firstUnreadIndex === -1) {
            this.scrollToBottom(true);
            return;
        }

        // Scroll to the first unread message
        const unreadIndex = firstUnreadIndex + 1;
        if (unreadIndex < this.interactions.length) {
            const messageElement = this.messageElements.get(unreadIndex);
            if (messageElement) {
                messageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                
                // Add unread indicator
                this.showUnreadIndicator(this.interactions.length - unreadIndex);
            }
        } else {
            this.scrollToBottom(true);
        }
    }

    showUnreadIndicator(unreadCount) {
        // Remove existing indicator
        const existing = this.viewport.querySelector('.unread-indicator');
        if (existing) existing.remove();

        if (unreadCount <= 0) return;

        const indicator = document.createElement('div');
        indicator.className = 'unread-indicator';
        indicator.textContent = `${unreadCount} unread message${unreadCount > 1 ? 's' : ''}`;
        
        // Position it before the first unread message
        const firstUnreadElement = this.messageElements.get(this.interactions.length - unreadCount);
        if (firstUnreadElement) {
            firstUnreadElement.parentNode.insertBefore(indicator, firstUnreadElement);
        }

        // Auto-hide after 5 seconds
        setTimeout(() => {
            if (indicator.parentNode) {
                indicator.remove();
            }
        }, 5000);
    }

    addOptimisticInteraction(payload) {
        if (this.isRendering) {
            // Queue the operation if we're currently rendering
            this.renderQueue.push(() => this.addOptimisticInteraction(payload));
            this.processRenderQueue();
            return;
        }

        const currentUser = this.appClient.getSharedData('user');
        const optimisticInteraction = {
            _id: payload.clientTempId,
            clientTempId: payload.clientTempId,
            conversationId: this.conversationData._id,
            timestamp: new Date().toISOString(),
            direction: 'outbound',
            sender: { name: currentUser.firstName || currentUser.username },
            payload: payload.content,
            status: 'sending'
        };

        this.interactions.push(optimisticInteraction);
        this.pendingOptimisticMessages.set(payload.clientTempId, optimisticInteraction);
        this.newestLoadedTimestamp = optimisticInteraction.timestamp;

        // Add the message element immediately
        this._addMessageElement(optimisticInteraction, this.interactions.length - 1);
        this.scrollToBottom(true);
    }

    confirmOptimisticInteraction(tempId, serverInteraction) {
        const optimisticIndex = this.interactions.findIndex(i => i.clientTempId === tempId);
        if (optimisticIndex > -1) {
            // Update the interaction data
            this.interactions[optimisticIndex] = serverInteraction;
            this.pendingOptimisticMessages.delete(tempId);
            this.newestLoadedTimestamp = serverInteraction.timestamp;

            // Update the existing element
            const element = this.messageElements.get(optimisticIndex);
            if (element) {
                this._updateMessageElement(element, serverInteraction);
            }
        }
    }

    handleRealtimeEvent(eventData) {
        if (eventData.type === 'newInteraction' && eventData.interaction) {
            const newInteraction = eventData.interaction;
            
            // Avoid duplicates
            const existingIndex = this.interactions.findIndex(i => i._id === newInteraction._id);
            if (existingIndex === -1) {
                // Check if this is newer than our newest loaded message
                if (!this.newestLoadedTimestamp || 
                    new Date(newInteraction.timestamp) > new Date(this.newestLoadedTimestamp)) {
                    
                    this.interactions.push(newInteraction);
                    this.newestLoadedTimestamp = newInteraction.timestamp;
                    this._addMessageElement(newInteraction, this.interactions.length - 1);
                    
                    // Only auto-scroll if user is near bottom
                    this.smartScrollToBottom();
                }
            }
        }
    }

    smartScrollToBottom() {
        if (!this.viewport) return;

        const scrollTop = this.viewport.scrollTop;
        const scrollHeight = this.viewport.scrollHeight;
        const clientHeight = this.viewport.clientHeight;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

        // If user is within 100px of bottom, auto-scroll
        if (distanceFromBottom < 100) {
            this.scrollToBottom(false);
        }
    }

    async render() {
        if (this.isRendering) return this.container;
        this.isRendering = true;

        try {
            this.container.innerHTML = '';
            this.messageElements.clear();

            if (!this.conversationData) {
                this.container.innerHTML = `<div class="timeline-placeholder"><i class="bi bi-chat-left-dots-fill"></i><h4>Select a conversation</h4></div>`;
                return this.container;
            }

            const header = this.renderHeader();
            this.container.appendChild(header);

            this.statusBannerContainer = document.createElement('div');
            this.statusBannerContainer.id = 'timeline-status-banner-container';
            this.container.appendChild(this.statusBannerContainer);
            this.updateStatusBanners();

            // Re-create viewport and messages container
            this.container.appendChild(this.viewport);
            this.messagesContainer.innerHTML = '';

            // Render all messages
            this.interactions.forEach((interaction, index) => {
                this._addMessageElement(interaction, index);
            });

            this.container.appendChild(this.composer.render());

        } finally {
            this.isRendering = false;
            this.processRenderQueue(); // Process any queued operations
        }

        return this.container;
    }

    processRenderQueue() {
        if (this.isProcessingRenderQueue || this.isRendering) return;
        
        this.isProcessingRenderQueue = true;
        
        while (this.renderQueue.length > 0) {
            const operation = this.renderQueue.shift();
            try {
                operation();
            } catch (error) {
                this.log.error('Error processing render queue operation:', error);
            }
        }
        
        this.isProcessingRenderQueue = false;
    }

    _addMessageElement(messageData, index) {
        if (!this.messagesContainer || !messageData) return;

        const wrapper = document.createElement('div');
        wrapper.className = 'timeline-message-wrapper';
        wrapper.dataset.messageIndex = index;

        this._updateMessageElement(wrapper, messageData);

        // Always append to maintain chronological order (oldest to newest)
        this.messagesContainer.appendChild(wrapper);
        this.messageElements.set(index, wrapper);
    }

    _updateMessageElement(wrapper, messageData) {
        if (!wrapper || !messageData) return;

        if (messageData.channelType === 'voice' && messageData.payload?.type) {
            wrapper.className = 'timeline-log-entry-wrapper';
            wrapper.innerHTML = this._renderVoiceLogEntry(messageData);
            return;
        }

        const isOutbound = messageData.direction === 'outbound';
        const isPending = messageData.status === 'sending'
            || this.pendingOptimisticMessages.has(messageData.clientTempId);
        const isEmail = messageData.channelType === 'email';
        const isSystemMessage = messageData.senderId === 'system';

        // Reset & reapply wrapper classes
        wrapper.className = 'timeline-message-wrapper';
        wrapper.classList.toggle('outbound', isOutbound && !isSystemMessage);
        wrapper.classList.toggle('inbound', !isOutbound && !isSystemMessage);
        wrapper.classList.toggle('system', isSystemMessage);

        // Build bubble CSS classes
        const bubbleClasses = [
            'timeline-message-bubble',
            isSystemMessage ? 'system' : (isOutbound ? 'outbound' : 'inbound'),
            isPending ? 'sending' : '',
            isEmail ? 'email-bubble' : ''
        ].filter(Boolean).join(' ');

        // Sender line for inbound messages
        let senderHtml = '';
        if (!isOutbound && !isSystemMessage && messageData.sender && messageData.sender.name) {
            const nameEsc = this._escapeHtml(messageData.sender.name);
            const emailEsc = messageData.sender.email
                ? ` <span class="sender-email">&lt;${this._escapeHtml(messageData.sender.email)}&gt;</span>`
                : '';
            senderHtml = `<div class="message-sender-name">${nameEsc}${emailEsc}</div>`;
        }

        // Subject line (emails only)
        let subjectHtml = '';
        if (isEmail && messageData.payload.subject) {
            subjectHtml = `<div class="email-subject">${this._escapeHtml(messageData.payload.subject)}</div>`;
        }

        // Body / content
        let contentHtml = '';
        if (isEmail) {
            const rawHtml = messageData.payload.html;
            if (rawHtml) {
                contentHtml = `<div class="email-content">${rawHtml}</div>`;
            } else {
                contentHtml = this._formatEmailBody(messageData.payload.body || '');
            }
        } else {
            const text = messageData.payload.text || messageData.payload.body || '';
            if (text) {
                try {
                    contentHtml = marked.parse(text);
                } catch (err) {
                    console.warn('Markdown parse failed:', err);
                    contentHtml = this._escapeHtml(text);
                }
            }
        }

        // Timestamp
        const time = new Date(messageData.timestamp)
            .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        // Assemble the inner HTML
        wrapper.innerHTML = `
            <div class="${bubbleClasses}">
                ${senderHtml}
                ${subjectHtml}
                ${isEmail
                    ? contentHtml
                    : `<div class="message-content">${contentHtml}</div>`
                }
                <div class="message-timestamp">${time}</div>
            </div>
        `;
    }

    _renderVoiceLogEntry(messageData) {
        const { payload, direction, timestamp } = messageData;
        const time = new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        let iconClass = '';
        let logText = '';

        switch (payload.type) {
            case 'call_started':
                iconClass = direction === 'inbound' ? 'bi-telephone-inbound-fill text-success' : 'bi-telephone-outbound-fill text-primary';
                logText = direction === 'inbound'
                    ? `Inbound call from ${payload.from} at ${time}`
                    : `Outbound call to ${payload.to} at ${time}`;
                break;
            case 'call_ended':
                iconClass = 'bi-telephone-x-fill text-danger';
                const minutes = Math.floor(payload.durationSeconds / 60);
                const seconds = payload.durationSeconds % 60;
                logText = `Call ended at ${time} (Duration: ${minutes}m ${seconds}s)`;
                break;
            case 'recording_available':
                if (this.recordingPlaybackKey) {
                    const secureAudioUrl = `/download/${this.recordingPlaybackKey}?file=${encodeURIComponent(payload.recordingUrl)}`;
                    return `
                        <div class="timeline-log-entry">
                            <div class="audio-player">
                                <i class="bi bi-mic-fill log-icon"></i>
                                <span>${this.t('Call Recording')}</span>
                                <audio controls controlsList="nodownload" src="${secureAudioUrl}"></audio>
                            </div>
                        </div>
                    `;
                } else {
                    return `
                        <div class="timeline-log-entry">
                            <i class="bi bi-exclamation-triangle-fill text-danger log-icon"></i>
                            <span>${this.t('Error loading recording (No Key)')}</span>
                        </div>
                    `;
                }
            default:
                iconClass = 'bi-info-circle-fill';
                logText = `Voice event: ${payload.type}`;
        }

        return `
            <div class="timeline-log-entry">
                <i class="bi ${iconClass} log-icon"></i>
                <span>${this.t(logText)}</span>
            </div>
        `;
    }

    scrollToBottom(force = false) {
        if (!this.viewport) return;

        // Clear any existing scroll timeout
        if (this.scrollTimeout) {
            clearTimeout(this.scrollTimeout);
        }

        const doScroll = () => {
            if (!this.viewport) return;

            const scrollHeight = this.viewport.scrollHeight;
            const clientHeight = this.viewport.clientHeight;
            const currentScrollTop = this.viewport.scrollTop;
            const distanceFromBottom = scrollHeight - currentScrollTop - clientHeight;

            if (force || distanceFromBottom < 100) {
                this.viewport.scrollTop = scrollHeight;
            }
        };

        if (force) {
            // Multiple attempts to ensure scroll happens after DOM updates
            requestAnimationFrame(() => {
                doScroll();
                requestAnimationFrame(doScroll);
            });
        } else {
            this.scrollTimeout = setTimeout(doScroll, 50);
        }
    }

    updateStatusBanners() {
        if (!this.statusBannerContainer || !this.conversationData) return;

        this.statusBannerContainer.innerHTML = '';

        if (this.conversationData.controlledBy === 'workflow') {
            const banner = document.createElement('div');
            banner.className = 'timeline-status-banner alert alert-info d-flex justify-content-between align-items-center p-2';
            banner.innerHTML = `
                <span><i class="bi bi-robot me-2"></i> ${this.t('This conversation is handled by an AI Assistant.')}</span>
                <button class="btn btn-sm btn-outline-primary" id="take-over-btn">${this.t('Take Over')}</button>
            `;
            banner.querySelector('#take-over-btn').addEventListener('click', () => {
                this.emit('humanTakeoverRequested', { conversationId: this.conversationData._id });
            });
            this.statusBannerContainer.appendChild(banner);
        } else if (this.conversationData.handoffState === 'ringing') {
            const banner = document.createElement('div');
            banner.className = 'timeline-status-banner alert alert-warning d-flex justify-content-between align-items-center p-2';
            banner.innerHTML = `
                <span><span class="spinner-border spinner-border-sm me-2"></span> ${this.t('Handoff requested. Ringing for agents...')}</span>
                <div class="btn-group btn-group-sm">
                    <button class="btn btn-success" id="accept-handoff-btn">${this.t('Accept')}</button>
                </div>
            `;
            banner.querySelector('#accept-handoff-btn').addEventListener('click', () => {
                this.emit('handoffAccepted', { conversationId: this.conversationData._id });
            });
            this.statusBannerContainer.appendChild(banner);
        }
        else if (this.conversationData.handoffState === 'accepted' && this.conversationData.assigneeId) {
            const assigneeName = this.conversationData.assigneeDisplayName || 'An agent';
            const banner = document.createElement('div');
            banner.className = 'timeline-status-banner alert alert-success p-2';
            banner.innerHTML = `
                <span><i class="bi bi-check-circle-fill me-2"></i> ${this.t('{{assigneeName}} has joined the conversation.', { assigneeName })}</span>
            `;
            this.statusBannerContainer.appendChild(banner);
        }
    }

    renderHeader() {
        const header = document.createElement('div');
        header.className = 'timeline-header';

        if (!this.conversationData) {
            return header;
        }

        const contact = this.conversationData.contact || {};
        const displayName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || contact.email || 'Unknown Contact';
        const avatar = createAvatarElement({ displayName, sizeClass: 'default' });

        const formatTimeSince = (date) => {
            if (!date) return '';
            const seconds = Math.floor((new Date() - new Date(date)) / 1000);
            if (seconds < 60) return this.t('Just now');
            const minutes = Math.floor(seconds / 60);
            if (minutes < 60) return this.t('{{count}}m ago', { count: minutes });
            const hours = Math.floor(minutes / 60);
            if (hours < 24) return this.t('{{count}}h ago', { count: hours });
            const days = Math.floor(hours / 24);
            return this.t('{{count}}d ago', { count: days });
        };

        const lastResponseTime = formatTimeSince(this.conversationData.lastInteractionTimestamp);
        const sidebarButtonHtml = `<button class="btn btn-link d-md-none" data-action="open-sidebar" title="${this.t('Conversation Details')}"><i class="bi bi-info-circle-fill"></i></button>`;

        header.innerHTML = `
            <button class="btn btn-link mobile-back-btn p-0 fs-4"><i class="bi bi-arrow-left"></i></button>
            ${avatar.outerHTML}
            <div class="timeline-header-info">
                <div class="timeline-header-name">${this._escapeHtml(displayName)}</div>
                <div class="timeline-header-subtext">
                    ${this.t('Status')}: ${this.t(this.conversationData.status)} &middot; ${lastResponseTime}
                </div>
            </div>
            <div class="ms-auto btn-group btn-group-sm gap-2">
                ${sidebarButtonHtml}
            </div>
        `;

        // Add event listeners safely
        const backBtn = header.querySelector('.mobile-back-btn');
        if (backBtn) {
            backBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.emit('closeTimeline');
            });
        }

        const sidebarBtn = header.querySelector('[data-action="open-sidebar"]');
        if (sidebarBtn) {
            sidebarBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.emit('sidebarToggled');
            });
        }

        return header;
    }

    _formatEmailBody(body) {
        if (!body || !body.trim()) {
            return '<div class="email-content"><em>No content</em></div>';
        }

        const clean = body.trim();

        // If it already looks like HTML, inject it verbatim
        const htmlTagRe = /<\/?[a-z][\s\S]*?>/i;
        if (htmlTagRe.test(clean)) {
            return `<div class="email-content">${clean}</div>`;
        }

        // Split off quoted content for plaintext emails
        const quotedPatterns = [
            /^On .+ wrote:$/m,
            /^From: .+$/m,
            /^Sent: .+$/m,
            /^To: .+$/m,
            /^Subject: .+$/m,
            /^-+ Original Message -+$/m,
            /^>+/m
        ];
        let splitIdx = -1;
        quotedPatterns.forEach(p => {
            const m = clean.match(p);
            if (m && typeof m.index === 'number') {
                if (splitIdx === -1 || m.index < splitIdx) splitIdx = m.index;
            }
        });

        let mainText = clean;
        let quotedText = '';
        if (splitIdx > 0) {
            mainText = clean.slice(0, splitIdx).trim();
            quotedText = clean.slice(splitIdx).trim();
        }

        // Helper to escape & paragraph-ify
        const escapeAndPara = txt => {
            let escaped = this._escapeHtml(txt);
            escaped = escaped.replace(/\n\s*\n/g, '</p><p>');
            escaped = escaped.replace(/\n/g, '<br>');
            return `<p>${escaped}</p>`;
        };

        // Build the main content block
        let html = `<div class="email-content">${escapeAndPara(mainText)}</div>`;

        // Add collapsible quoted content if present
        if (quotedText && quotedText.length > 20) {
            const q = escapeAndPara(quotedText);
            html += `
                <details class="email-quoted-content">
                    <summary class="email-quote-header">
                        <i class="bi bi-chevron-down"></i> Previous messages
                    </summary>
                    <div class="email-original-message">
                        ${q}
                    </div>
                </details>
            `;
        }

        return html;
    }

    // Utility method to jump to a specific message by ID
    scrollToMessage(messageId) {
        const index = this.interactions.findIndex(i => i._id === messageId);
        if (index === -1) return false;

        const element = this.messageElements.get(index);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return true;
        }
        return false;
    }

    // Mark conversation as read up to a specific message
    markAsReadUpTo(messageId) {
        const index = this.interactions.findIndex(i => i._id === messageId);
        if (index === -1) return;

        // Remove unread indicator if it exists
        const indicator = this.viewport.querySelector('.unread-indicator');
        if (indicator) {
            indicator.remove();
        }

        // Emit event to update read status on server
        this.emit('markAsRead', { 
            conversationId: this.conversationData._id, 
            messageId: messageId 
        });
    }

    // Auto-mark as read when scrolled to bottom
    checkAndMarkAsRead() {
        if (!this.viewport || !this.conversationData) return;

        const scrollTop = this.viewport.scrollTop;
        const scrollHeight = this.viewport.scrollHeight;
        const clientHeight = this.viewport.clientHeight;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

        // If scrolled to bottom and there are interactions
        if (distanceFromBottom < 10 && this.interactions.length > 0) {
            const lastMessage = this.interactions[this.interactions.length - 1];
            if (lastMessage && lastMessage.direction === 'inbound') {
                this.markAsReadUpTo(lastMessage._id);
            }
        }
    }

    // Force reload conversation from scratch
    async reloadConversation() {
        if (!this.conversationData) return;

        const conversationId = this.conversationData._id;
        
        // Reset all state
        this.interactions = [];
        this.messageElements.clear();
        this.pendingOptimisticMessages.clear();
        this.hasMoreOlder = true;
        this.hasMoreNewer = false;
        this.oldestLoadedTimestamp = null;
        this.newestLoadedTimestamp = null;
        this.scrollToBottomOnNextRender = true;

        // Reload the conversation data
        try {
            const updatedConversation = await this.appClient.rpcClient.call('UnifiedInboxRpc.getConversation', {
                conversationId: conversationId
            });
            
            if (updatedConversation) {
                await this.setConversation(updatedConversation);
            }
        } catch (error) {
            this.log.error('Failed to reload conversation:', error);
        }
    }

    // Get debug information
    getDebugInfo() {
        return {
            conversationId: this.conversationData?._id,
            interactionCount: this.interactions.length,
            hasMoreOlder: this.hasMoreOlder,
            hasMoreNewer: this.hasMoreNewer,
            isLoadingOlder: this.isLoadingOlder,
            isLoadingNewer: this.isLoadingNewer,
            oldestTimestamp: this.oldestLoadedTimestamp,
            newestTimestamp: this.newestLoadedTimestamp,
            pendingOptimisticCount: this.pendingOptimisticMessages.size,
            renderQueueLength: this.renderQueue.length,
            isRendering: this.isRendering,
            scrollPosition: this.viewport ? {
                scrollTop: this.viewport.scrollTop,
                scrollHeight: this.viewport.scrollHeight,
                clientHeight: this.viewport.clientHeight
            } : null
        };
    }

    // Refresh the entire timeline - useful for debugging or force refresh
    refresh() {
        if (this.conversationData) {
            this.render();
            this.scrollToBottom(true);
        }
    }

    // Get message count for debugging
    getMessageCount() {
        return this.interactions.length;
    }

    // Clear all pending optimistic messages (useful for error recovery)
    clearPendingMessages() {
        this.pendingOptimisticMessages.clear();
        // Re-render to remove sending states
        if (this.messagesContainer) {
            this.messageElements.forEach((element, index) => {
                if (this.interactions[index]) {
                    this._updateMessageElement(element, this.interactions[index]);
                }
            });
        }
    }

    _escapeHtml(unsafe) {
        if (typeof unsafe !== 'string') return '';
        return unsafe
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");
    }

    destroy() {
        // Clear all timeouts
        if (this.scrollTimeout) {
            clearTimeout(this.scrollTimeout);
            this.scrollTimeout = null;
        }
        if (this.scrollThrottle) {
            clearTimeout(this.scrollThrottle);
            this.scrollThrottle = null;
        }

        // Clear all state
        this.messageElements.clear();
        this.pendingOptimisticMessages.clear();
        this.renderQueue = [];
        this.interactions = [];
        this.conversationData = null;
        this.isRendering = false;
        this.isProcessingRenderQueue = false;

        // Call parent destroy
        if (super.destroy) {
            super.destroy();
        }
    }
}